import type { CardSavePhase } from './CardSaveBadge';
import { CardSaveBadge } from './CardSaveBadge';
import { ReferralCaptureFields } from './ReferralCaptureFields';
import { ReferralProgramPromo } from './ReferralProgramPromo';
import { isPersonBasedReferralSource } from '../../constants/referralSources';

const DEFAULT_TITLE = 'pr-8 text-lg font-bold text-gray-900 dark:text-white';
const DEFAULT_DESCRIPTION = 'mt-1 text-sm text-gray-500 dark:text-gray-400';
const DEFAULT_FIELD =
  'w-full rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2.5 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400';

const DEFAULT_CARD_TITLE = 'How did you hear about us?';
const DEFAULT_CARD_DESCRIPTION = 'Optional — tell us how you heard about Amber Peak.';

function referralCaptureComplete(referrerName: string, referrerSource: string): boolean {
  const source = referrerSource.trim();
  if (!source) return false;
  if (isPersonBasedReferralSource(source)) {
    return referrerName.trim().length > 0;
  }
  return true;
}

interface ReferralCardProps {
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  fieldClass?: string;
  savePhase?: CardSavePhase;
  title?: string;
  description?: string;
  /** Lead with the $100 referral offer (My Information and apply flows). */
  showReferralPromo?: boolean;
  /** Narrow bento column beside notifications on My Information. */
  compactColumn?: boolean;
  /** Keep listbox/name inputs from stretching across wide group cards (lease signing). */
  limitFieldWidth?: boolean;
  /** Optional capture — no completion checkmark; does not count toward step progress. */
  optional?: boolean;
  referrerName: string;
  referrerSource: string;
  onReferrerNameChange: (value: string) => void;
  onReferrerSourceChange: (value: string) => void;
  nameId?: string;
  sourceId?: string;
}

export function ReferralCard({
  className,
  titleClassName = DEFAULT_TITLE,
  descriptionClassName = DEFAULT_DESCRIPTION,
  fieldClass = DEFAULT_FIELD,
  savePhase,
  title = DEFAULT_CARD_TITLE,
  description = DEFAULT_CARD_DESCRIPTION,
  showReferralPromo = true,
  compactColumn = false,
  limitFieldWidth = false,
  optional = true,
  referrerName,
  referrerSource,
  onReferrerNameChange,
  onReferrerSourceChange,
  nameId,
  sourceId,
}: ReferralCardProps) {
  const complete = referralCaptureComplete(referrerName, referrerSource);
  const cappedWidthClass = limitFieldWidth ? 'max-w-sm w-full' : undefined;

  return (
    <section className={className}>
      {savePhase ? <CardSaveBadge phase={savePhase} complete={optional ? false : complete} /> : null}
      <h2 className={titleClassName}>{title}</h2>
      {!showReferralPromo ? <p className={descriptionClassName}>{description}</p> : null}
      {showReferralPromo ? <ReferralProgramPromo compactColumn={compactColumn} /> : null}
      <div className={`min-h-0 ${compactColumn ? 'mt-3 flex-1' : 'mt-4'}`}>
        <ReferralCaptureFields
          referrerName={referrerName}
          referrerSource={referrerSource}
          onReferrerNameChange={onReferrerNameChange}
          onReferrerSourceChange={onReferrerSourceChange}
          fieldClass={fieldClass}
          nameId={nameId}
          sourceId={sourceId}
          compactColumn={compactColumn}
          fieldMaxWidthClass={cappedWidthClass}
        />
      </div>
    </section>
  );
}
